// Unsaved changes and going back (DESIGN.md, "Navigation: breadcrumbs and back"). An editor
// holding changes says so with `useUnsavedChanges(dirty)`. Back (Esc, Alt+Left, the mouse's Back
// button, a back link or crumb, the browser's own Back) then asks first: "Discard changes?",
// Discard or Keep editing. `DiscardChangesDialog` draws the question once per app.

import { useEffect, useRef, useSyncExternalStore } from "react";

/** Editors holding unsaved changes, each with how to save now (when it can). */
const dirty = new Map<symbol, (() => unknown) | undefined>();
/**
 * Editors whose changes the user just chose to discard. They don't count while the navigation
 * they allowed takes them off screen; one still on screen after RELEASE_MS counts again.
 */
const released = new Set<symbol>();
const RELEASE_MS = 1500;

/** True while an editor on screen holds changes it hasn't saved. */
export const hasUnsavedChanges = () => [...dirty.keys()].some((t) => !released.has(t));

/**
 * Mark this editor as holding unsaved changes while `isDirty` is true. `flush`, when given, saves
 * them now (`flushUnsaved` calls it when the app leaves on its own, as after a call).
 */
export function useUnsavedChanges(isDirty: boolean, flush?: () => unknown) {
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    if (!isDirty) return;
    const token = Symbol("unsaved");
    dirty.set(token, () => flushRef.current?.());
    return () => {
      dirty.delete(token);
      released.delete(token);
    };
  }, [isDirty]);
}

/** Save what can be saved now, without waiting (the editors' own autosave covers the rest). */
export function flushUnsaved() {
  for (const flush of dirty.values()) {
    try {
      void Promise.resolve(flush?.()).catch(() => {});
    } catch {
      // A failed save is the editor's to show.
    }
  }
}

// ---------- the question ----------

let asking: Promise<boolean> | null = null;
let answer: ((discard: boolean) => void) | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/**
 * Ask before leaving unsaved changes. Resolves true at once when there are none, else with the
 * answer: true for Discard (the changes are let go), false for Keep editing. Asking twice while
 * the question is open gets the same answer.
 */
export function confirmDiscard(): Promise<boolean> {
  if (!hasUnsavedChanges()) return Promise.resolve(true);
  if (asking) return asking;
  asking = new Promise<boolean>((resolve) => {
    answer = resolve;
  });
  notify();
  return asking;
}

/** Answer the open question (the dialog's buttons, or a test). */
export function answerDiscard(discard: boolean) {
  const done = answer;
  asking = null;
  answer = null;
  // Discarded: the editors on screen are about to go, so nothing asks about them on the way
  // out. Any still on screen once the move is done count again.
  if (discard) {
    const now = [...dirty.keys()];
    for (const t of now) released.add(t);
    setTimeout(() => {
      for (const t of now) released.delete(t);
    }, RELEASE_MS);
  }
  notify();
  done?.(discard);
}

/** Whether the question is open (for the dialog). */
export function useDiscardQuestion(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => asking !== null,
    () => false,
  );
}

/** Run `go` once the user agrees to leave (at once when nothing is unsaved). */
export async function leaveIfAllowed(go: () => void): Promise<boolean> {
  if (!(await confirmDiscard())) return false;
  go();
  return true;
}

/** The history's blocker, as TanStack Router's history takes it. */
export interface BlockableHistory {
  block: (b: {
    blockerFn: (a: { action: string }) => boolean | Promise<boolean>;
    enableBeforeUnload?: boolean | (() => boolean);
  }) => () => void;
}

/**
 * The router's blocker for the browser's own Back and Forward (a popstate): it asks first while
 * something is unsaved. The app's own back paths ask before they move (`leaveIfAllowed`), so a
 * push or a replace is never held here: a save that navigates as it finishes must still go.
 */
export function blockBackWhileUnsaved(history: BlockableHistory): () => void {
  return history.block({
    blockerFn: async ({ action }) => {
      if (action === "PUSH" || action === "REPLACE") return false;
      return !(await confirmDiscard());
    },
    enableBeforeUnload: false,
  });
}

/** `useUnsavedChanges` as an element, for a form that reads its dirty flag in a render prop. */
export function UnsavedChanges({ dirty: isDirty }: { dirty: boolean }): null {
  useUnsavedChanges(isDirty);
  return null;
}
